/**
 * Hero-asset seam (LOCKED-DESIGN 18.3): the game's asset strategy is HYBRID —
 * a procedural environment plus a minimal GLTF pipeline for hero pieces only
 * (player character, the three receiver tiers, the furnace maw, plus the
 * reject-release handle the recovery fiction hangs on).
 *
 * No human 3D artist exists yet, so every hero ships as a HIGH-DETAIL
 * PROCEDURAL kit-bash behind this seam:
 *
 *  - `createHeroAsset(id)`   — synchronous procedural build (today's reality).
 *  - `loadHeroAsset(id)`     — async: tries `public/models/<id>.glb` through a
 *                              lazy-imported GLTFLoader, falls back to the
 *                              procedural factory when the model is absent.
 *  - `upgradeHeroAsset(id)`  — fire-and-forget GLTF-only probe used by call
 *                              sites that must construct synchronously: they
 *                              mount the procedural asset immediately and swap
 *                              in the authored model IF one exists. Dropping a
 *                              .glb into public/models/ therefore requires
 *                              ZERO call-site rework.
 *
 * Every asset exposes named HARDPOINTS (Map<string, Object3D>) — the cosmetic
 * / FX attach points of 18.3 (shells, plating, decals, beam effects mount
 * here) plus `rig:*` articulation nodes the renderer animates (squash-stretch
 * stays procedural; no skeletal stack is built). Authored .glb files declare
 * the same contract with empties named `hp_<name>` (`:` → `-` is accepted).
 *
 * Visual-only: nothing here touches ArenaPhysics or the simulation. All colors
 * come from theme.ts (HERO_THEME); cosmetic randomness is seeded (18.8).
 */
import * as THREE from "three";
import type { QualityLevel } from "@host/types.js";
import { HERO_THEME, RECEIVER_TIER_COLORS, TEXTURE_THEME } from "./theme.js";

export type HeroAssetId =
  | "player-unit"
  | "receiver-safe"
  | "receiver-technical"
  | "receiver-hazard"
  | "furnace-maw"
  | "release-handle";

/** Procedural fallback sizing (authored .glb files arrive pre-sized). */
export interface HeroAssetParams {
  /** aperture / assembly width in world units */
  width?: number;
  /** aperture / assembly height in world units */
  height?: number;
}

export interface HeroAsset {
  id: HeroAssetId;
  source: "procedural" | "gltf";
  root: THREE.Group;
  /** named cosmetic/FX attach points + `rig:*` articulation nodes (18.3) */
  hardpoints: Map<string, THREE.Object3D>;
  dispose(): void;
}

/**
 * Resolve a live emissive material across both hero sources.
 *
 * Procedural assets attach their material object to a hardpoint's userData.
 * glTF extras cannot serialize a Three.js Material reference, so authored
 * assets expose the same binding through a contract-named mesh instead.
 */
export function resolveHeroGlowMaterial(
  asset: HeroAsset,
  hardpointName: string,
  meshName: string,
): THREE.MeshStandardMaterial | null {
  const hardpointGlow = asset.hardpoints.get(hardpointName)?.userData.glow;
  if (hardpointGlow instanceof THREE.MeshStandardMaterial) return hardpointGlow;
  const mesh = asset.root.getObjectByName(meshName) as THREE.Mesh | null;
  if (!mesh?.isMesh) return null;
  const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
  return materials.find(
    (material): material is THREE.MeshStandardMaterial =>
      material instanceof THREE.MeshStandardMaterial,
  ) ?? null;
}

export interface FurnaceShimmerBinding {
  mesh: THREE.Mesh;
  material: THREE.MeshBasicMaterial;
  texture: THREE.Texture;
}

/**
 * Bind the live furnace shimmer across procedural and authored sources.
 *
 * A GLB cannot preserve the procedural CanvasTexture or its additive
 * MeshBasicMaterial. The authored model therefore supplies only the named
 * shimmer plane; this seam recreates the runtime-only material after load.
 */
export function resolveFurnaceShimmer(
  asset: HeroAsset,
  textureFactory: () => THREE.Texture | null = makeShimmerTexture,
): FurnaceShimmerBinding | null {
  const mesh = asset.root.getObjectByName("furnace-heat-shimmer") as THREE.Mesh | null;
  if (!mesh?.isMesh) return null;
  if (mesh.material instanceof THREE.MeshBasicMaterial && mesh.material.map) {
    return { mesh, material: mesh.material, texture: mesh.material.map };
  }
  const texture = textureFactory();
  if (!texture) return null;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.repeat.set(2, 1);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    opacity: HERO_THEME.furnace.shimmer.opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  mesh.userData.replacedFurnaceShimmerMaterial = mesh.material;
  mesh.material = material;
  mesh.userData.heroDetail = true;
  return { mesh, material, texture };
}

/**
 * Local z the maw's throat plane has to reach: the arena mounts the furnace as
 * a shallow relief on the chamber's SOLID north wall, whose face sits only
 * ~0.08 behind the piece origin (arena.v1.json), so the throat reads only when
 * it stands just proud of that origin.
 */
export const FURNACE_THROAT_MOUNT_Z = 0.035;

/**
 * Seat a furnace maw so its throat clears the wall it is mounted on.
 *
 * The hero contract lets an authored maw model a REAL recessed cavity, and the
 * authored GLB uses it: its `furnace-throat-fire` plane sits ~0.4 BEHIND the
 * mount origin. Mounted as exported that buries the fire inside the north wall
 * and the chamber loses its focal glow (2.1 / dogfood #1) — the wall renders
 * where the fire should be. Push the root forward by exactly the shortfall so
 * the throat lands at FURNACE_THROAT_MOUNT_Z, where the procedural build
 * already puts it. Idempotent, and a no-op for any asset already seated.
 * Returns the applied offset.
 */
export function seatFurnaceThroat(asset: HeroAsset): number {
  const fire = asset.root.getObjectByName("furnace-throat-fire") as THREE.Mesh | null;
  if (!fire?.isMesh || !fire.geometry) return 0;
  asset.root.updateWorldMatrix(false, true);
  const throat = new THREE.Box3().setFromObject(fire).applyMatrix4(
    asset.root.matrixWorld.clone().invert(),
  );
  if (throat.isEmpty()) return 0;
  const offset = FURNACE_THROAT_MOUNT_Z - (asset.root.position.z + throat.min.z);
  if (!Number.isFinite(offset) || offset <= 1e-4) return 0;
  asset.root.position.z += offset;
  return offset;
}

/** The hardpoint contract every source (procedural or GLTF) must satisfy. */
export const HERO_HARDPOINTS: Record<HeroAssetId, readonly string[]> = {
  "player-unit": ["head", "shoulderL", "shoulderR", "back", "beamEmitter"],
  "receiver-safe": ["intake", "fx"],
  "receiver-technical": ["intake", "fx"],
  "receiver-hazard": ["intake", "fx"],
  "furnace-maw": ["throat", "fx"],
  "release-handle": ["pull", "lamp"],
};

export interface HeroLoadOptions {
  /**
   * Injectable GLTF fetch (unit tests stub this). Resolves the model's scene
   * root, or null when no authored model exists for the id. The default
   * implementation HEAD-probes `<baseUrl><id>.glb` and lazy-imports
   * GLTFLoader only when a real model answers.
   */
  loadGltf?: (url: string) => Promise<THREE.Object3D | null>;
  /** model directory (default "models/" under the site root = public/models) */
  baseUrl?: string;
}

/**
 * Async seam: authored GLTF first, procedural fallback. This is the ONE code
 * path future .glb drops flow through; the unit suite proves both branches.
 */
export async function loadHeroAsset(id: HeroAssetId, params?: HeroAssetParams, options?: HeroLoadOptions): Promise<HeroAsset> {
  const gltf = await tryLoadGltf(id, options);
  return gltf ?? createHeroAsset(id, params);
}

/**
 * Fire-and-forget GLTF upgrade for synchronous call sites: mounts nothing by
 * itself — when (and only when) an authored model exists, `onReady` receives
 * the loaded asset so the call site can swap its procedural stand-in out.
 */
export function upgradeHeroAsset(id: HeroAssetId, onReady: (asset: HeroAsset) => void, options?: HeroLoadOptions): void {
  void tryLoadGltf(id, options).then((asset) => {
    if (asset) onReady(asset);
  });
}

async function tryLoadGltf(id: HeroAssetId, options?: HeroLoadOptions): Promise<HeroAsset | null> {
  const url = `${options?.baseUrl ?? "models/"}${id}.glb`;
  const load = options?.loadGltf ?? defaultGltfLoad;
  try {
    const scene = await load(url);
    if (scene) return gltfHeroAsset(id, scene);
  } catch {
    /* absent or malformed model → procedural fallback */
  }
  return null;
}

/**
 * Known-absent model cache: the arena editor rebuilds GrayBoxArena on every
 * edit, and six probes per rebuild would spam the dev server. A miss is final
 * for the session (drop a model in → reload picks it up).
 */
const gltfAbsent = new Set<string>();

async function defaultGltfLoad(url: string): Promise<THREE.Object3D | null> {
  if (typeof fetch !== "function" || gltfAbsent.has(url)) return null;
  const probe = await fetch(url, { method: "HEAD" }).catch(() => null);
  const contentType = probe?.headers.get("content-type") ?? "";
  // vite's dev server answers unknown paths with index.html — treat as absent
  if (!probe?.ok || contentType.includes("text/html")) {
    gltfAbsent.add(url);
    return null;
  }
  const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
  const gltf = await new GLTFLoader().loadAsync(url);
  return gltf.scene;
}

/** Wrap an authored GLTF scene in the HeroAsset contract. */
function gltfHeroAsset(id: HeroAssetId, scene: THREE.Object3D): HeroAsset {
  const root = new THREE.Group();
  root.name = `hero-${id}`;
  root.add(scene);
  const hardpoints = new Map<string, THREE.Object3D>();
  scene.traverse((node) => {
    if (node.name.startsWith("hp_")) hardpoints.set(node.name.slice(3).replace("rig-", "rig:"), node);
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh) mesh.castShadow = true;
  });
  // synthesize any missing contract hardpoints at the origin so cosmetics/FX
  // always have a mount, even on a sloppy export
  for (const name of HERO_HARDPOINTS[id]) {
    if (!hardpoints.has(name)) addHardpoint(hardpoints, root, name, 0, 0, 0);
  }
  return { id, source: "gltf", root, hardpoints, dispose: () => disposeHero(root) };
}

/** Synchronous procedural kit-bash build (today's only real source). */
export function createHeroAsset(id: HeroAssetId, params: HeroAssetParams = {}): HeroAsset {
  const hardpoints = new Map<string, THREE.Object3D>();
  const root = new THREE.Group();
  root.name = `hero-${id}`;
  PROCEDURAL_FACTORIES[id](root, hardpoints, params);
  return { id, source: "procedural", root, hardpoints, dispose: () => disposeHero(root) };
}

/**
 * Quality-tier hook (LOCKED-DESIGN 13.1): hero factories tag garnish meshes
 * `userData.heroDetail`; the low tier hides them (and any animated treatments
 * they carry, e.g. the furnace heat shimmer). Never touches structure meshes,
 * so silhouettes and QA seams read identically on every tier.
 */
export function applyHeroQuality(root: THREE.Object3D, level: QualityLevel): void {
  const low = level === "low" || level === "potato";
  root.traverse((node) => {
    if (node.userData.heroDetail === true) node.visible = !low;
  });
}

/** Dispose every geometry/material/texture under a hero root and unmount it. */
function disposeHero(root: THREE.Object3D): void {
  root.parent?.remove(root);
  const disposed = new Set<THREE.Material>();
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const material = mesh.material as THREE.Material | THREE.Material[] | undefined;
    const replaced = mesh.userData
      .replacedFurnaceShimmerMaterial as THREE.Material | THREE.Material[] | undefined;
    const entries = [
      ...(Array.isArray(material) ? material : material ? [material] : []),
      ...(Array.isArray(replaced) ? replaced : replaced ? [replaced] : []),
    ];
    for (const entry of entries) {
      if (disposed.has(entry)) continue;
      disposed.add(entry);
      for (const value of Object.values(entry)) {
        if (value instanceof THREE.Texture) value.dispose();
      }
      entry.dispose();
    }
  });
}

// ---------------------------------------------------------------------------
// procedural factories
// ---------------------------------------------------------------------------

type HeroFactory = (root: THREE.Group, hardpoints: Map<string, THREE.Object3D>, params: HeroAssetParams) => void;

interface MaterialTheme {
  color: number;
  roughness: number;
  metalness: number;
  emissive?: number;
  emissiveIntensity?: number;
}

function std(theme: MaterialTheme, overrides: Partial<THREE.MeshStandardMaterialParameters> = {}): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: theme.color,
    roughness: theme.roughness,
    metalness: theme.metalness,
    emissive: theme.emissive ?? 0x000000,
    emissiveIntensity: theme.emissiveIntensity ?? 0,
    ...overrides,
  });
}

function addHardpoint(map: Map<string, THREE.Object3D>, parent: THREE.Object3D, name: string, x: number, y: number, z: number): THREE.Object3D {
  const point = new THREE.Object3D();
  point.name = `hp_${name.replace("rig:", "rig-")}`;
  point.position.set(x, y, z);
  parent.add(point);
  map.set(name, point);
  return point;
}

function part(parent: THREE.Object3D, geometry: THREE.BufferGeometry, material: THREE.Material, x: number, y: number, z: number, name = ""): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.set(x, y, z);
  if (name) mesh.name = name;
  parent.add(mesh);
  return mesh;
}

/** Seeded PRNG (mulberry32) — cosmetic decals never vary run to run (18.8). */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const HERO_DECAL_SEED = 0x53553037; // "SU07"

/**
 * Facility-livery chest decal: stencilled unit number over a worn plate with a
 * hazard-stripe band. Seeded; null under headless test runtimes (no canvas).
 */
function makeUnitDecal(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const rand = mulberry32(HERO_DECAL_SEED);
  const theme = HERO_THEME.robot.decal;
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 96;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = theme.plate;
  ctx.fillRect(0, 0, 128, 96);
  // hazard stripe band across the bottom of the plate
  ctx.fillStyle = theme.stripeDark;
  ctx.fillRect(0, 72, 128, 24);
  ctx.fillStyle = theme.stripe;
  for (let x = -24; x < 152; x += 32) {
    ctx.beginPath();
    ctx.moveTo(x, 96); ctx.lineTo(x + 12, 96); ctx.lineTo(x + 28, 72); ctx.lineTo(x + 16, 72);
    ctx.closePath(); ctx.fill();
  }
  // stencilled unit number
  ctx.fillStyle = theme.ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = '800 34px "Arial Narrow", Arial, sans-serif';
  ctx.fillText(theme.unit, 64, 38);
  // seeded wear speckle
  ctx.fillStyle = theme.wear;
  for (let i = 0; i < 26; i++) ctx.fillRect(rand() * 128, rand() * 96, 1 + rand() * 3, 1 + rand() * 2);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

/** Diagonal hazard chevrons for the hazard-receiver warning plates (seeded n/a — pure geometry). */
function makeChevronTexture(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 64;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = TEXTURE_THEME.hazard.base;
  ctx.fillRect(0, 0, 128, 64);
  ctx.fillStyle = TEXTURE_THEME.hazard.stripe;
  for (let x = -32; x < 160; x += 32) {
    ctx.beginPath();
    ctx.moveTo(x, 64); ctx.lineTo(x + 12, 64); ctx.lineTo(x + 28, 0); ctx.lineTo(x + 16, 0);
    ctx.closePath(); ctx.fill();
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/** Rising heat-shimmer sheet for the furnace throat (additive, scrolled). */
function makeShimmerTexture(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const rand = mulberry32(HERO_DECAL_SEED ^ 0x66);
  const theme = HERO_THEME.furnace.shimmer;
  const canvas = document.createElement("canvas");
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, 128, 128);
  for (let i = 0; i < 26; i++) {
    const x = rand() * 128;
    const w = 3 + rand() * 9;
    ctx.fillStyle = rand() < 0.5 ? theme.hot : theme.soft;
    // wavy vertical streak drawn as stacked offset slabs
    let drift = 0;
    for (let y = 0; y < 128; y += 8) {
      drift += (rand() - 0.5) * 5;
      ctx.fillRect(x + drift, y, w, 8);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  return texture;
}

/**
 * PLAYER-UNIT — mechanical sanitation robot with personality (18.3: WALL-E
 * lineage, playful-industrial). Local space matches the retired capsule: the
 * body spans y ∈ [-0.9, 0.9] around the physics-body center, facing +z.
 * `rig:flex` carries the procedural squash-stretch; `rig:head` aims the visor
 * at the aim point; `rig:emitter` is the beam-emitter arm that tracks the held
 * load and glows with beam state (its core material rides `userData.glow`).
 */
const buildPlayerUnit: HeroFactory = (root, hardpoints) => {
  const theme = HERO_THEME.robot;
  const hull = std(theme.hull);
  const livery = std(theme.livery);
  const joint = std(theme.joint);
  const tread = std(theme.tread);

  const flex = new THREE.Group();
  flex.name = "hp_rig-flex";
  root.add(flex);
  hardpoints.set("rig:flex", flex);

  // --- tracked base: stubby treads + drive hubs (silhouette anchor) ---
  for (const side of [-1, 1] as const) {
    part(flex, new THREE.BoxGeometry(0.26, 0.34, 0.78), tread, side * 0.28, -0.71, 0, "robot-tread");
    for (const z of [-0.26, 0, 0.26]) {
      const hub = part(flex, new THREE.CylinderGeometry(0.1, 0.1, 0.06, 10), joint, side * 0.32, -0.71, z, "robot-tread-hub");
      hub.rotation.z = Math.PI / 2;
      hub.userData.heroDetail = true;
    }
  }
  part(flex, new THREE.BoxGeometry(0.5, 0.24, 0.42), joint, 0, -0.46, 0, "robot-pelvis");

  // --- boxy torso with facility livery ---
  part(flex, new THREE.BoxGeometry(0.74, 0.66, 0.5), hull, 0, -0.02, 0, "robot-torso");
  part(flex, new THREE.BoxGeometry(0.6, 0.36, 0.06), livery, 0, 0.02, 0.26, "robot-chest-plate");
  const decal = makeUnitDecal();
  if (decal) {
    const decalMat = new THREE.MeshStandardMaterial({ map: decal, roughness: 0.55, metalness: 0.15 });
    part(flex, new THREE.PlaneGeometry(0.44, 0.32), decalMat, 0, 0.01, 0.295, "robot-livery-decal");
  }

  // --- shoulders + backpack (cosmetic hardpoints live here) ---
  for (const side of [-1, 1] as const) {
    part(flex, new THREE.BoxGeometry(0.18, 0.3, 0.34), joint, side * 0.5, 0.18, 0, "robot-shoulder");
    addHardpoint(hardpoints, flex, side < 0 ? "shoulderL" : "shoulderR", side * 0.62, 0.26, 0);
  }
  part(flex, new THREE.BoxGeometry(0.5, 0.44, 0.18), livery, 0, 0.05, -0.32, "robot-backpack");
  for (const side of [-1, 1] as const) {
    const stack = part(flex, new THREE.CylinderGeometry(0.05, 0.05, 0.2, 8), joint, side * 0.14, 0.34, -0.34, "robot-exhaust");
    stack.userData.heroDetail = true;
  }
  addHardpoint(hardpoints, flex, "back", 0, 0.2, -0.44);

  // --- expressive head unit: visor eye aims where the player aims ---
  const head = new THREE.Group();
  head.name = "hp_rig-head";
  head.position.set(0, 0.46, 0);
  flex.add(head);
  hardpoints.set("rig:head", head);
  part(head, new THREE.BoxGeometry(0.46, 0.3, 0.4), hull, 0, 0.18, 0, "robot-head");
  part(head, new THREE.BoxGeometry(0.48, 0.08, 0.42), livery, 0, 0.36, 0, "robot-head-cap");
  const visor = part(head, new THREE.BoxGeometry(0.34, 0.14, 0.05), std(theme.visor), 0, 0.18, 0.2, "robot-visor");
  visor.userData.glow = visor.material;
  const antenna = part(head, new THREE.CylinderGeometry(0.02, 0.02, 0.3, 6), joint, 0.16, 0.5, -0.12, "robot-antenna");
  antenna.userData.heroDetail = true;
  const antennaTip = part(head, new THREE.SphereGeometry(0.035, 8, 6), std(theme.emitterCore, { emissiveIntensity: 1.4 }), 0.16, 0.66, -0.12, "robot-antenna-tip");
  antennaTip.userData.heroDetail = true;
  addHardpoint(hardpoints, head, "head", 0, 0.44, 0);

  // --- articulated beam-emitter arm/dish (tracks the held load) ---
  const emitter = new THREE.Group();
  emitter.name = "hp_rig-emitter";
  emitter.position.set(0.56, 0.34, 0.02);
  flex.add(emitter);
  hardpoints.set("rig:emitter", emitter);
  const arm = part(emitter, new THREE.CylinderGeometry(0.045, 0.045, 0.3, 8), joint, 0, 0, 0.15, "robot-emitter-arm");
  arm.rotation.x = Math.PI / 2;
  const dish = part(emitter, new THREE.CylinderGeometry(0.16, 0.05, 0.16, 12, 1, true), std(theme.emitter, { side: THREE.DoubleSide }), 0, 0, 0.34, "robot-emitter-dish");
  dish.rotation.x = Math.PI / 2;
  const core = part(emitter, new THREE.SphereGeometry(0.07, 10, 8), std(theme.emitterCore, { emissiveIntensity: theme.emitterIdle }), 0, 0, 0.38, "robot-emitter-core");
  core.userData.glow = core.material;
  emitter.userData.glow = core.material;
  addHardpoint(hardpoints, emitter, "beamEmitter", 0, 0, 0.44);

  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh) mesh.castShadow = true;
  });
};

/**
 * Receiver factory local frame: XY is the aperture plane, +z points along the
 * receiver normal (out of the wall/floor/ceiling). GrayBoxArena positions the
 * root at the aperture center and applies portalSurfaceOrientation, exactly
 * like every other receiver visual.
 */

/** SAFE — broad low intake: gentle funnel, guard rails, wide approach (3.2). */
const buildReceiverSafe: HeroFactory = (root, hardpoints, params) => {
  const radius = Math.min(params.width ?? 4.4, params.height ?? 4.4) / 2;
  const theme = HERO_THEME.receiver;
  const accent = RECEIVER_TIER_COLORS.safe;
  const guard = std(theme.guard);
  const signal = std({ color: accent, roughness: 0.38, metalness: 0.48, emissive: accent, emissiveIntensity: 0.18 }, { side: THREE.DoubleSide, transparent: true });

  // gentle funnel: a wide, shallow flare rising from the rim
  const funnel = part(root, new THREE.CylinderGeometry(radius + 0.55, radius + 0.05, 0.5, 28, 1, true), std(theme.funnel, { side: THREE.DoubleSide }), 0, 0, 0.27, "safe-funnel");
  funnel.rotation.x = Math.PI / 2;
  // tier accent rim ring at the throat
  part(root, new THREE.RingGeometry(radius + 0.02, radius + 0.3, 36), signal, 0, 0, 0.03, "safe-rim");
  // guard rails: low posts + side rails, the "safe to approach" vocabulary
  // (cylinders default to a local-y axis, which is already the rail direction)
  for (const sx of [-1, 1] as const) {
    for (const sy of [-0.62, 0.62] as const) {
      const post = part(root, new THREE.CylinderGeometry(0.06, 0.08, 0.72, 10), guard, sx * (radius + 0.72), sy * (radius + 0.3), 0.36, "safe-rail-post");
      post.rotation.x = Math.PI / 2;
    }
    part(root, new THREE.CylinderGeometry(0.05, 0.05, radius * 1.5, 10), guard, sx * (radius + 0.72), 0, 0.72, "safe-rail");
  }
  // wide approach runway stripe (the old locator language, kept)
  part(root, new THREE.PlaneGeometry(radius * 1.75, 0.18), signal, 0, -radius - 0.42, 0.02, "safe-runway");
  // intake grate bars (garnish — dropped on the low tier)
  for (const y of [-0.8, 0, 0.8]) {
    const bar = part(root, new THREE.BoxGeometry(radius * 1.7, 0.07, 0.04), std(theme.guardDark), 0, y * radius * 0.5, 0.05, "safe-grate");
    bar.userData.heroDetail = true;
  }
  addHardpoint(hardpoints, root, "intake", 0, 0, 0.1);
  addHardpoint(hardpoints, root, "fx", 0, 0, 0.6);
};

/** TECHNICAL — narrow side-wall aperture: precision shrouds + alignment fins. */
const buildReceiverTechnical: HeroFactory = (root, hardpoints, params) => {
  const w = params.width ?? 4;
  const h = params.height ?? 4.6;
  const theme = HERO_THEME.receiver;
  const accent = RECEIVER_TIER_COLORS.technical;
  const guard = std(theme.guard);
  const guardDark = std(theme.guardDark);
  const signal = std({ color: accent, roughness: 0.38, metalness: 0.48, emissive: accent, emissiveIntensity: 0.3 }, { side: THREE.DoubleSide, transparent: true });

  // machined collar frame around the slot
  for (const sy of [-1, 1] as const) part(root, new THREE.BoxGeometry(w + 0.75, 0.3, 0.34), guard, 0, sy * (h / 2 + 0.26), 0.1, "tech-collar-h");
  for (const sx of [-1, 1] as const) part(root, new THREE.BoxGeometry(0.3, h + 0.24, 0.34), guard, sx * (w / 2 + 0.32), 0, 0.1, "tech-collar-v");
  // precision shrouds: angled cheek plates converging on the slot
  for (const sx of [-1, 1] as const) {
    const shroud = part(root, new THREE.BoxGeometry(0.95, h + 0.4, 0.08), guardDark, sx * (w / 2 + 0.78), 0, 0.45, "tech-shroud");
    shroud.rotation.y = sx * 0.52;
  }
  // alignment fins along the top and bottom collars (garnish)
  for (const sy of [-1, 1] as const) {
    for (const fx of [-0.3, 0, 0.3]) {
      const fin = part(root, new THREE.BoxGeometry(0.05, 0.34, 0.5), guardDark, fx * w, sy * (h / 2 + 0.28), 0.32, "tech-fin");
      fin.userData.heroDetail = true;
    }
  }
  // amber precision edge strips flanking the slot (tier accent)
  for (const sx of [-1, 1] as const) part(root, new THREE.BoxGeometry(0.1, h + 0.24, 0.06), signal, sx * (w / 2 + 0.1), 0, 0.14, "tech-edge-strip");
  addHardpoint(hardpoints, root, "intake", 0, 0, 0.1);
  addHardpoint(hardpoints, root, "fx", 0, 0, 0.6);
};

/**
 * HAZARD — overhead assembly: heavy collar, guard cage, warning chevrons.
 * Local +z is the intake normal (world DOWN for the authored overhead
 * receiver), so the cage extends along -z, up toward the hazard deck. Kept
 * radially tight: the arena-camera sightline to the aperture center must stay
 * clear (qa:arena intent check), so nothing solid enters the inner cone.
 *
 * The cage also opens a MOUTH toward local -y: the authored hazard-baffle
 * (arena.v1.json) pivots 1.95 units along -y from the aperture center and its
 * 4.2-unit bar + emissive tips sweep a ~2.13-radius circle straight through
 * the cage band (local z ≈ -0.07..-0.76). Any bar or plate inside that
 * annulus gets skewered every revolution (3.2 timing readability / 18.3 hero
 * bar), so cage bars and chevron plates keep BAFFLE_SWEEP_CLEARANCE from the
 * pivot — HeroAssets.test.ts locks the clearance numerically.
 */
/** Authored baffle pivot in hazard-hero local space (world (4.82, 3, -16.5)). */
export const BAFFLE_SWEEP_PIVOT_Y = -1.95;
/** Swept reach (bar/tip corner ≈2.13) + element radius + safety margin. */
export const BAFFLE_SWEEP_CLEARANCE = 2.25;
/** Cage band along local z the sweep passes through (assembly ± margin). */
export const BAFFLE_SWEEP_Z_BAND: readonly [number, number] = [-0.76, -0.07];

const buildReceiverHazard: HeroFactory = (root, hardpoints, params) => {
  const radius = Math.min(params.width ?? 4, params.height ?? 4) / 2;
  const theme = HERO_THEME.receiver;
  const accent = RECEIVER_TIER_COLORS.hazard;
  const guard = std(theme.guard);
  const signal = std({ color: accent, roughness: 0.38, metalness: 0.48, emissive: accent, emissiveIntensity: 0.42 }, { side: THREE.DoubleSide, transparent: true });

  // heavy octagonal collar (the 8-sided identity the old ring carried)
  const collarRadius = radius + 0.3;
  const segment = collarRadius * 2 * Math.tan(Math.PI / 8) * 0.98;
  for (let i = 0; i < 8; i++) {
    const angle = (i / 8) * Math.PI * 2 + Math.PI / 8;
    const beam = part(root, new THREE.BoxGeometry(segment, 0.26, 0.22), guard, Math.cos(angle) * collarRadius, Math.sin(angle) * collarRadius, -0.06, "hazard-collar");
    beam.rotation.z = angle + Math.PI / 2;
  }
  // guard cage bars rising toward the deck (heavier guard structure) — packed
  // over the arc that clears the baffle sweep; the mouth opens toward the
  // gate mechanism (local -y, see the keep-out comment above)
  const mouthCenter = -Math.PI / 2;
  const mouthHalf = THREE.MathUtils.degToRad(70);
  for (let i = 0; i < 8; i++) {
    const angle = mouthCenter + mouthHalf + ((i + 0.5) / 8) * (Math.PI * 2 - mouthHalf * 2);
    const bar = part(root, new THREE.CylinderGeometry(0.05, 0.05, 0.92, 8), guard, Math.cos(angle) * collarRadius, Math.sin(angle) * collarRadius, -0.52, "hazard-cage-bar");
    bar.rotation.x = Math.PI / 2;
  }
  // upper cage ring (garnish)
  const topRing = part(root, new THREE.TorusGeometry(collarRadius, 0.04, 6, 24), std(theme.guardDark), 0, 0, -0.96, "hazard-cage-ring");
  topRing.userData.heroDetail = true;
  // warning chevron plates facing outward on three sides. Each rides a
  // carrier group rotated in the aperture plane, so in carrier space +x is
  // radial, +y tangential, and z runs along the intake normal
  // (world-vertical here). The mouth side (local -y) carries none — a plate
  // there sits inside the baffle sweep, and the bar's own emissive tips ARE
  // that side's moving warning.
  const chevron = makeChevronTexture();
  const chevronMat = chevron
    ? new THREE.MeshStandardMaterial({ map: chevron, roughness: 0.7, metalness: 0.2, transparent: true })
    : std(HERO_THEME.receiver.chevronFallback, { transparent: true });
  for (let i = 0; i < 3; i++) {
    const carrier = new THREE.Group();
    carrier.name = "hazard-chevron-carrier";
    carrier.rotation.z = (i / 4) * Math.PI * 2;
    root.add(carrier);
    part(carrier, new THREE.BoxGeometry(0.05, 1.25, 0.42), chevronMat, collarRadius + 0.08, 0, -0.4, "hazard-chevron-plate");
  }
  // descent markers: emissive chevron cones aimed into the throat (tier accent)
  for (const angle of [-Math.PI / 3, 0, Math.PI / 3]) {
    const cone = part(root, new THREE.ConeGeometry(0.18, 0.52, 3), signal, Math.sin(angle) * (radius + 0.5), Math.cos(angle) * (radius + 0.5), 0.04, "hazard-marker");
    cone.rotation.z = -angle;
  }
  addHardpoint(hardpoints, root, "intake", 0, 0, 0.1);
  addHardpoint(hardpoints, root, "fx", 0, 0, 0.8);
};

/**
 * FURNACE-MAW — the chamber's focal point (2.1): a segmented door/teeth frame
 * around a glowing throat with a heat-shimmer treatment, intake rollers, and a
 * grate apron. Local XY is the maw plane, +z faces the arena. The throat fire
 * plane is named `furnace-throat-fire` (GrayBoxArena drives its flicker) and
 * the shimmer sheet `furnace-heat-shimmer` (scrolled; heroDetail → dropped on
 * the low tier). Incineration must read as ENTRY INTO THE MAW (dogfood #1).
 */
const buildFurnaceMaw: HeroFactory = (root, hardpoints, params) => {
  const w = params.width ?? 9.4;
  const h = params.height ?? 3.1;
  const theme = HERO_THEME.furnace;

  // dark throat backplate
  part(root, new THREE.PlaneGeometry(w, h), std(theme.throatBack), 0, 0, 0, "furnace-throat-back");
  // glowing fire plane (GrayBoxArena owns the flicker via this mesh's material)
  const fireTexture = makeFurnaceGradientTexture();
  const fireMat = std(theme.fire, { transparent: true });
  if (fireTexture) {
    fireMat.map = fireTexture;
    fireMat.emissiveMap = fireTexture;
  }
  part(root, new THREE.PlaneGeometry(w * 0.84, h * 0.5), fireMat, 0, 0, FURNACE_THROAT_MOUNT_Z, "furnace-throat-fire");
  // rising heat-shimmer sheet (additive, scrolled in animate; low tier drops it)
  const shimmerTexture = makeShimmerTexture();
  if (shimmerTexture) {
    shimmerTexture.repeat.set(2, 1);
    const shimmer = part(
      root,
      new THREE.PlaneGeometry(w * 0.8, h * 0.62),
      new THREE.MeshBasicMaterial({ map: shimmerTexture, transparent: true, opacity: theme.shimmer.opacity, blending: THREE.AdditiveBlending, depthWrite: false }),
      0, h * 0.08, 0.06, "furnace-heat-shimmer",
    );
    shimmer.userData.heroDetail = true;
  }
  // segmented teeth across the maw head (the "door" identity)
  const toothMat = std(theme.tooth);
  const teeth = 8;
  for (let i = 0; i < teeth; i++) {
    const x = -w / 2 + ((i + 0.5) * w) / teeth;
    const tooth = part(root, new THREE.ConeGeometry(w / teeth / 2.2, 0.72, 4), toothMat, x, h * 0.42, 0.12, "furnace-tooth");
    tooth.rotation.z = Math.PI; // point down into the throat
    tooth.rotation.y = Math.PI / 4;
    tooth.scale.z = 0.5;
  }
  // header + side gussets framing the segments
  const frameMat = std(theme.frame);
  part(root, new THREE.BoxGeometry(w * 0.98, 0.34, 0.4), frameMat, 0, h * 0.52, 0.08, "furnace-header");
  for (const sx of [-1, 1] as const) part(root, new THREE.BoxGeometry(0.42, h * 0.86, 0.36), frameMat, sx * (w / 2 - 0.28), 0, 0.08, "furnace-gusset");
  // hot ember strip under the teeth (bloom accent — the warmest anchor, 18.1)
  part(root, new THREE.BoxGeometry(w * 0.84, 0.06, 0.05), std(theme.emberStrip), 0, h * 0.28, 0.1, "furnace-ember-strip");
  // intake rollers stepping down toward the arena floor + grate apron
  const rollerMat = std(theme.roller);
  for (const [index, [y, z]] of ([[-h * 0.36, 0.12], [-h * 0.44, 0.3], [-h * 0.52, 0.48]] as const).entries()) {
    const roller = part(root, new THREE.CylinderGeometry(0.14, 0.14, w * 0.86, 14), rollerMat, 0, y, z, `furnace-roller-${index + 1}`);
    roller.rotation.z = Math.PI / 2;
  }
  const grate = part(root, new THREE.BoxGeometry(w * 0.86, 0.07, 0.5), std(theme.frame), 0, -h * 0.3, 0.3, "furnace-grate");
  grate.userData.heroDetail = true;
  addHardpoint(hardpoints, root, "throat", 0, 0, 0.2);
  addHardpoint(hardpoints, root, "fx", 0, -h * 0.25, 0.6);
  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh && mesh.name !== "furnace-heat-shimmer") mesh.castShadow = false;
  });
};

/** Banded heat gradient for the throat (static; flicker is material-level). */
function makeFurnaceGradientTexture(): THREE.CanvasTexture | null {
  if (typeof document === "undefined") return null;
  const theme = HERO_THEME.furnace.gradient;
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 192;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = theme.base;
  ctx.fillRect(0, 0, 512, 192);
  const heat = ctx.createLinearGradient(0, 0, 0, 192);
  heat.addColorStop(0, theme.deep);
  heat.addColorStop(0.38, theme.mid);
  heat.addColorStop(0.7, theme.hot);
  heat.addColorStop(1, theme.top);
  ctx.fillStyle = heat;
  ctx.fillRect(18, 24, 476, 150);
  ctx.fillStyle = theme.bar;
  for (let x = 32; x < 512; x += 64) ctx.fillRect(x, 18, 13, 162);
  ctx.fillStyle = theme.sill;
  ctx.fillRect(18, 148, 476, 18);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * RELEASE-HANDLE — re-dressed as the MAW FEED GUARD in m1.3.0 (LOCKED-DESIGN
 * 18.12): the same guard-post assembly (base plate, pivot housing, arm, indicator
 * lamp) now stands at the intake lip marking the FEED POINT. The `lamp` hardpoint's
 * mesh carries the signal material (`userData.glow`) that GrayBoxArena.setJamActive
 * lights teal while a jam waits. The legacy `pull`/`rig:pivot` hardpoints remain on
 * the asset (unused — the m1.3.0 recovery is hand-feeding, not a lever pull).
 */
const buildReleaseHandle: HeroFactory = (root, hardpoints, params) => {
  const height = params.height ?? 1.7;
  const half = height / 2;
  const theme = HERO_THEME.handle;
  const base = std(theme.base);
  const arm = std(theme.arm);
  const signal = std(theme.signal);

  // base plate + pivot housing cheeks + axle
  part(root, new THREE.BoxGeometry(0.72, 0.1, 0.55), base, 0, -half + 0.05, 0, "handle-base-plate");
  for (const sx of [-1, 1] as const) part(root, new THREE.BoxGeometry(0.08, 0.36, 0.4), base, sx * 0.15, -half + 0.28, 0, "handle-cheek");
  const axle = part(root, new THREE.CylinderGeometry(0.055, 0.055, 0.44, 10), std(theme.grip), 0, -half + 0.34, 0, "handle-axle");
  axle.rotation.z = Math.PI / 2;

  // pivot arm: the pull-animation hook (GrayBoxArena drives rotation.x)
  const pivot = new THREE.Group();
  pivot.name = "hp_rig-pivot";
  pivot.position.set(0, -half + 0.34, 0);
  root.add(pivot);
  hardpoints.set("rig:pivot", pivot);
  const armLength = height - 0.5;
  part(pivot, new THREE.BoxGeometry(0.11, armLength, 0.11), arm, 0, armLength / 2 + 0.05, 0, "handle-arm");
  // teal signal stripe up the arm face (shares the lamp's pulsing material)
  part(pivot, new THREE.BoxGeometry(0.035, armLength * 0.72, 0.035), signal, 0, armLength / 2 + 0.05, 0.075, "handle-arm-stripe");
  const grip = part(pivot, new THREE.CylinderGeometry(0.085, 0.085, 0.46, 12), std(theme.grip), 0, armLength + 0.08, 0, "handle-grip");
  grip.rotation.z = Math.PI / 2;
  for (const sx of [-1, 1] as const) {
    const cap = part(pivot, new THREE.SphereGeometry(0.09, 10, 8), base, sx * 0.23, armLength + 0.08, 0, "handle-grip-cap");
    cap.userData.heroDetail = true;
  }
  addHardpoint(hardpoints, pivot, "pull", 0, armLength + 0.08, 0);

  // indicator lamp mast on the base (the jam-active interactable cue)
  part(root, new THREE.BoxGeometry(0.09, 0.55, 0.09), base, 0.3, -half + 0.38, -0.14, "handle-lamp-mast");
  const lens = part(root, new THREE.SphereGeometry(0.075, 12, 10), signal, 0.3, -half + 0.72, -0.14, "handle-lamp-lens");
  lens.userData.glow = signal;
  const lampPoint = addHardpoint(hardpoints, root, "lamp", 0.3, -half + 0.72, -0.14);
  lampPoint.userData.glow = signal;

  root.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (mesh.isMesh) mesh.castShadow = true;
  });
};

const PROCEDURAL_FACTORIES: Record<HeroAssetId, HeroFactory> = {
  "player-unit": buildPlayerUnit,
  "receiver-safe": buildReceiverSafe,
  "receiver-technical": buildReceiverTechnical,
  "receiver-hazard": buildReceiverHazard,
  "furnace-maw": buildFurnaceMaw,
  "release-handle": buildReleaseHandle,
};
